import React from 'react';
import type { PageEntry, Node } from 'contensis-delivery-api';
import htmlParser from 'html-react-parser';
import dashify from 'dashify';

import cmsParser from 'Common/util/cmsParser';
import { sanitizePhoneNumber } from 'Common/util/string';

import type { Props } from './Academic';

export type PageData = {
    entry: PageEntry;
    ancestors: Node[];
};

const datamap = (data: PageData): Props => {
    const { entry, ancestors } = data;
    const {
        title,
        sys,
        staffMember,
        // biography
        biographyHeading,
        biographyContent,
        // courses
        coursesHeading,
        taughtCourses,
        // futherInformation
        furtherInformationHeading,
        furtherInfromationContent: furtherInformationContent
    } = entry;

    return {
        breadcrumbs: cmsParser.breadcrumbs([...ancestors, { entry: { title }, id: sys.id }]),
        heroBanner: {
            firstName: staffMember.person.forename,
            lastName: staffMember.person.surname,
            honorific: staffMember.honorific ? staffMember.honorific.name : undefined,
            postNominalTitles:
                staffMember.postnominalTitles.length > 0
                    ? staffMember.postnominalTitles
                    : undefined,
            jobTitle: staffMember.jobTitle,
            department: entry.department.name,
            image: staffMember.person.picture
                ? cmsParser.image(staffMember.person.picture)
                : undefined,
            email:
                staffMember.person.email && !staffMember.person.hideEmail
                    ? {
                          label: staffMember.person.email,
                          ariaLabel: `Email ${staffMember.person.email}`,
                          href: `mailto:${staffMember.person.email}`
                      }
                    : undefined,
            phone: staffMember.person.phoneNumber
                ? {
                      label: staffMember.person.phoneNumber,
                      ariaLabel: `Phone ${staffMember.person.phoneNumber}`,
                      href: `tel:${sanitizePhoneNumber(staffMember.person.phoneNumber)}`
                  }
                : undefined,
            roomLocation: staffMember.room,
            socialLinks:
                staffMember.person.socialMediaLink.length > 0
                    ? Object.fromEntries(
                          staffMember.person.socialMediaLink.map(item => [
                              item.socialMediaType,
                              item.link
                          ])
                      )
                    : undefined
        },
        biography: {
            heading: biographyHeading,
            content: htmlParser(biographyContent)
        },
        courses: {
            heading: coursesHeading,
            cards:
                taughtCourses && taughtCourses.length > 0
                    ? taughtCourses.map(course => ({
                          id: course.sys.id,
                          image: cmsParser.image(course.courseCardImage),
                          heading:
                              course.degreeType && course.degreeType !== 'none'
                                  ? `${course.degreeType} ${course.name}`
                                  : course.name,
                          children: <p>{course.courseDescription}</p>,
                          link: {
                              href: course.sys.uri,
                              label: 'View course',
                              ariaLabel: `View course${
                                  course.degreeType && course.degreeType !== 'none'
                                      ? ` ${course.degreeType}`
                                      : ''
                              } ${course.name}`
                          }
                      }))
                    : []
        },
        furtherInformation: {
            heading: furtherInformationHeading,
            items:
                furtherInformationContent && furtherInformationContent.length > 0
                    ? furtherInformationContent.map(item => ({
                          id: dashify(item.value.title),
                          button: {
                              ariaLabel: `View more about ${item.value.title}`
                          },
                          heading: item.value.title,
                          children: htmlParser(item.value.content),
                          isBackgroundSameColour: true
                      }))
                    : []
        }
    };
};

export default datamap;
